import React, { useMemo } from 'react';
import { Card, Typography, Space, Badge, Divider, Avatar, Tooltip, theme } from 'antd';
import { 
  TrophyOutlined, 
  UserOutlined,
  ThunderboltOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface Participant {
  id: string;
  name: string;
}

interface BracketPreviewProps {
  participants: Participant[];
  enableThirdPlace: boolean;
}

interface RoundPreview {
  roundNumber: number;
  roundName: string;
  matchCount: number;
  byeCount: number;
}

const nextPowerOf2 = (n: number): number => {
  if (n <= 1) return 1;
  let power = 1;
  while (power < n) {
    power *= 2;
  }
  return power;
};

const getRoundName = (roundsFromFinal: number): string => {
  switch (roundsFromFinal) {
    case 0: return 'Finale';
    case 1: return 'Polufinale';
    case 2: return 'Četvrtfinale';
    case 3: return 'Osmina finala';
    case 4: return 'Šesnaestina finala';
    case 5: return 'Krug od 64';
    default: return `Krug ${roundsFromFinal + 1}`;
  }
};

const BracketPreview: React.FC<BracketPreviewProps> = ({ participants, enableThirdPlace }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const bracketInfo = useMemo(() => {
    const participantCount = participants.length;
    const bracketSize = nextPowerOf2(participantCount);
    const byeCount = bracketSize - participantCount;
    const totalRounds = Math.log2(bracketSize);

    const rounds: RoundPreview[] = [];
    let matchesInRound = bracketSize / 2;
    const byesRemaining = byeCount;

    for (let i = 0; i < totalRounds; i++) {
      const roundsFromFinal = totalRounds - i - 1;
      const roundName = getRoundName(roundsFromFinal);
      const byeMatchesInRound = i === 0 ? byesRemaining : 0;
      
      rounds.push({
        roundNumber: i + 1,
        roundName,
        matchCount: matchesInRound,
        byeCount: byeMatchesInRound,
      });

      matchesInRound = matchesInRound / 2;
    }

    return { participantCount, bracketSize, byeCount, totalRounds, rounds };
  }, [participants]);

  if (participants.length < 4) {
    return (
      <Card size="small" style={{ marginBottom: 16, borderColor: token.colorWarningBorder }}>
        <Space>
          <ThunderboltOutlined style={{ color: '#faad14' }} />
          <Text type="warning">
            {t('tournament.minimumParticipantsRequired') || 'Minimum 4 participants required for bracket preview'}
          </Text>
        </Space>
      </Card>
    );
  }

  const totalMatches = bracketInfo.rounds.reduce((sum, r) => sum + r.matchCount, 0) + (enableThirdPlace ? 1 : 0);

  return (
    <Card 
      size="small" 
      style={{ marginBottom: 16 }}
      styles={{ 
        header: { 
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          padding: '12px 16px'
        },
        body: { 
          padding: 16 
        }
      }}
      title={
        <Space>
          <TrophyOutlined style={{ color: '#52c41a' }} />
          <Text strong>{t('tournament.bracketPreview') || 'Bracket Preview'}</Text>
        </Space>
      }
    >
      {/* Stats Row */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center',
        gap: 32, 
        marginBottom: 16,
        padding: '16px 24px',
        background: token.colorFillAlter,
        borderRadius: 8
      }}>
        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            {t('tournament.players', 'Players')}
          </Text>
          <Text strong style={{ fontSize: 24 }}>{bracketInfo.participantCount}</Text>
        </div>
        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            {t('tournament.rounds', 'Rounds')}
          </Text>
          <Text strong style={{ fontSize: 24 }}>{bracketInfo.totalRounds}</Text>
        </div>
        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            {t('tournament.matches', 'Matches')}
          </Text>
          <Text strong style={{ fontSize: 24 }}>{totalMatches}</Text>
        </div>
        {bracketInfo.byeCount > 0 && (
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {t('tournament.byes', 'BYEs')}
            </Text>
            <Text strong style={{ fontSize: 24, color: token.colorInfo }}>{bracketInfo.byeCount}</Text>
          </div>
        )}
      </div>

      {/* Tournament Flow */}
      <div style={{ marginBottom: 16, textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
          {t('tournament.tournamentFlow', 'Tournament Flow')}
        </Text>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 8,
          flexWrap: 'wrap'
        }}>
          {bracketInfo.rounds.map((round, index) => {
            const isFinal = index === bracketInfo.rounds.length - 1;
            return (
              <React.Fragment key={round.roundNumber}>
                <Tooltip title={`${round.matchCount} ${round.matchCount === 1 ? 'match' : 'matches'}${round.byeCount > 0 ? ` (${round.byeCount} BYE)` : ''}`}>
                  <Badge 
                    count={round.matchCount} 
                    size="small"
                    style={{ 
                      backgroundColor: isFinal ? token.colorSuccess : token.colorInfo
                    }}
                  >
                    <div style={{
                      padding: '6px 12px',
                      background: isFinal ? token.colorSuccessBg : token.colorBgContainer,
                      border: `1px solid ${isFinal ? token.colorSuccessBorder : token.colorBorder}`,
                      borderRadius: 6,
                      cursor: 'default',
                      fontWeight: isFinal ? 600 : 400,
                      fontSize: 13
                    }}>
                      {round.roundName}
                    </div>
                  </Badge>
                </Tooltip>
                {index < bracketInfo.rounds.length - 1 && (
                  <ArrowRightOutlined style={{ color: token.colorTextQuaternary, fontSize: 12 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Third Place Match Info */}
      {enableThirdPlace && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <div style={{ textAlign: 'center' }}>
            <Space>
              <TrophyOutlined style={{ color: '#fa8c16' }} />
              <Text style={{ fontSize: 13 }}>
                {t('tournament.thirdPlaceMatch', '3rd place playoff')}: {t('tournament.semiFinalsLosers', 'Semi-final losers compete for bronze')}
              </Text>
            </Space>
          </div>
        </>
      )}

      {/* Participants */}
      <Divider style={{ margin: '12px 0' }} />
      <div style={{ textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
          {t('tournament.participants', 'Participants')}
        </Text>
        <Avatar.Group 
          max={{ 
            count: 8,
            style: { 
              color: token.colorTextLightSolid, 
              backgroundColor: token.colorInfo,
              cursor: 'default'
            }
          }}
          size="small"
        >
          {participants.map((p, index) => (
            <Tooltip key={p.id} title={p.name || `Player ${index + 1}`}>
              <Avatar 
                size="small"
                style={{ 
                  backgroundColor: `hsl(${(index * 37) % 360}, 60%, 60%)`,
                  cursor: 'default'
                }}
                icon={<UserOutlined />}
              />
            </Tooltip>
          ))}
        </Avatar.Group>
      </div>
    </Card>
  );
};

export default BracketPreview;
